import * as React from "react";
import { useStatsRowStyles } from "../styles/statsRow.styles";
import type { StatsData } from "../types/StatsRow.types";
import { STATUS_LABEL } from "../constants/statusMapping";
import { DisplayTestStatus } from "../types/TestCasesTab.types";
import { primer } from "../styles/primerDarkTheme";

export type { StatsData };

interface StatCardProps {
    label: string;
    value: string | number;
    dotColor?: string;
    description?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, dotColor, description }) => {
    const s = useStatsRowStyles();
    return (
        <div className={s.card}>
            <div className={s.labelRow}>
                {dotColor && <span className={s.dot} style={{ backgroundColor: dotColor }} />}
                <span className={s.label}>{label}</span>
            </div>
            <span className={s.value}>{value}</span>
            {description && <span className={s.description}>{description}</span>}
        </div>
    );
};

function changeDescription(current: number, previous: number | undefined): string {
    if (previous === undefined) return "";
    if (previous === current) return "No changes from last run";
    if (previous === 0) return "100% more than last run";
    const delta = current - previous;
    const pct = Math.round(Math.abs((delta / previous) * 100));
    if (delta > 0) return `${pct}% more than last run`;
    if (current === 0) return "100% less than last run";
    return `${pct}% less than last run`;
}

export const StatsRow: React.FC<{ data: StatsData }> = ({ data }) => {
    const s = useStatsRowStyles();
    const suitesLabel = data.totalSuites === 1 ? "1 test suite" : `${data.totalSuites} test suites`;
    const prev = data.previousRun;
    return (
        <div className={s.row}>
            <StatCard
                label="Total Tests"
                value={data.totalTests}
                description={`across ${suitesLabel}`}
            />
            <StatCard
                label={STATUS_LABEL[DisplayTestStatus.Passed]}
                value={data.passed}
                dotColor={primer.green}
                description={changeDescription(data.passed, prev?.passed)}
            />
            <StatCard
                label={STATUS_LABEL[DisplayTestStatus.Failed]}
                value={data.failed}
                dotColor={primer.orange}
                description={changeDescription(data.failed, prev?.failed)}
            />
            <StatCard
                label={STATUS_LABEL[DisplayTestStatus.Flaky]}
                value={data.flaky}
                dotColor={primer.flakyYellow}
                description={changeDescription(data.flaky, prev?.flaky)}
            />
            <StatCard
                label={STATUS_LABEL[DisplayTestStatus.Skipped]}
                value={data.skipped}
                dotColor={primer.red}
                description={changeDescription(data.skipped, prev?.skipped)}
            />
        </div>
    );
};
